import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { getSiteCopy, submitPartner, referOnePagerUrl, formatApiErrorDetail } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { PAGE_SEO, breadcrumbLd } from "@/lib/seo";
import { track, getUTM } from "@/lib/analytics";
import { Check, Download } from "lucide-react";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const STATS = [
  { v: "$500", l: "Per closed engagement" },
  { v: "20%", l: "Referral rate. Industry standard is 5 to 15%" },
  { v: "$6,000", l: "A year from three referrals a quarter" },
];

export default function Refer() {
  const { data: copy = {} } = useQuery({ queryKey: ["site-copy"], queryFn: getSiteCopy });
  const [form, setForm] = useState({ name: "", email: "", serves: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setStatus("submitting");
    try {
      await submitPartner({ ...form, utm: getUTM() });
      track("brief_request", { offer: "referral-partner" });
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong.");
    }
  };

  return (
    <div className="exec-scope min-h-screen" data-testid="refer-page">
      <Seo {...PAGE_SEO.refer} jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "For advisors", path: "/refer" }])} />
      <section className="exec-container pt-24 md:pt-28 pb-10">
        <div className="max-w-[820px]">
          <p className="exec-eyebrow">For advisors</p>
          <h1 className="font-extralight text-[clamp(32px,4.4vw,56px)] leading-[1.1] mt-6">Refer a founder. Earn the rate that respects your network.</h1>
          <p data-testid="refer-who" className="exec-serif text-[clamp(19px,2.2vw,25px)] leading-[1.4] text-[color:var(--ink)] mt-6">
            {copy.refer_who || "For the advisors, coaches, and connectors whose clients keep asking the questions you are not positioned to answer."}
          </p>
          <p data-testid="refer-body" className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-6">{copy.refer_body}</p>
          <a href={referOnePagerUrl()} target="_blank" rel="noopener noreferrer" data-testid="refer-one-pager-link" onClick={() => track("resource_download", { resource: "Refer one-pager" })} className="exec-btn exec-btn-secondary mt-7">
            <Download size={14} /> Download the one-pager
          </a>
        </div>
      </section>

      <section className="exec-container pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {STATS.map((s) => (
            <motion.div key={s.v} {...fade} className="border-t border-[color:var(--rule)] pt-6">
              <p className="font-extralight text-[clamp(28px,3vw,38px)] leading-none text-[color:var(--ink)]">{s.v}</p>
              <p className="exec-serif text-[16px] leading-[1.5] text-[color:var(--lead-ink)] mt-3">{s.l}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Become a partner — light section (was sage) */}
      <section className="exec-container pb-24">
        <div className="exec-rule mb-12" />
        <div className="max-w-md">
          <p className="exec-eyebrow">Become a partner</p>
          <h2 className="font-light text-[clamp(26px,3.2vw,38px)] leading-[1.14] mt-4">Make the introduction.</h2>
          {status === "success" ? (
            <div data-testid="partner-success" className="mt-7 flex items-start gap-3">
              <span className="w-8 h-8 rounded-full border border-[color:var(--copper)] flex items-center justify-center shrink-0"><Check size={16} className="text-[color:var(--copper-deep)]" /></span>
              <p className="exec-serif text-[17px] text-[color:var(--lead-ink)]">You are in. We will be in touch about the partnership and the introduction.</p>
            </div>
          ) : (
            <form data-testid="partner-form" onSubmit={submit} className="mt-7 space-y-5">
              <input data-testid="partner-name" className="exec-underline" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input data-testid="partner-email" type="email" className="exec-underline" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input data-testid="partner-serves" className="exec-underline" placeholder="Who do you serve?" value={form.serves} onChange={(e) => setForm({ ...form, serves: e.target.value })} />
              {error && <p data-testid="partner-error" className="exec-serif text-[16px] text-[color:var(--ink)] border-l-2 border-[color:var(--copper)] pl-3">{error}</p>}
              <button data-testid="partner-submit" type="submit" disabled={status === "submitting"} className="exec-btn exec-btn-primary disabled:opacity-60">
                {status === "submitting" ? "…" : "Become a partner"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
